// BUILD-046 correction cycle 1bb88b3e (stage B1): the engine behind a list that is read page by page (see pagedListModel.ts). Framework-free so it is
// tested without a DOM; a view subscribes to it with useSyncExternalStore.
//
//   load()          : reads the FIRST page and replaces everything (mount, Retry, a reload after an accepted action, a changed filter).
//   ensureLoaded()  : load() only when nothing has been requested yet (a view calls it when it is shown).
//   loadMore()      : reads the page after the last loaded one and appends it; a failure keeps what is loaded.
//   patch()         : replaces one loaded item (a re-read entry); nothing is requested.
//   callerChanged() : everything the previous Caller had is cleared AT ONCE (back to idle). With `reloadOnCallerChange` (the default) and a list a
//                     view has asked for, the first page is read again for the new Caller. A list the new Caller may not be allowed to read (the
//                     completed Team Work list) sets it false: its view asks again once the new Caller's summary allows the view, so a Caller who
//                     is not an overseer is never sent a request that can only be refused (found by the browser run of task 9.39).
// An answer for an older request or an older Caller is dropped. A failure is never an empty list: it is `error`, or `unavailable` when the server does
// not serve the route.
//
// Erasable TypeScript; runtime imports: the paged model and the list-state helper that recognises a dropped (stale) answer.

import { isStaleOutcome } from './listStateModel';
import { initialPaged, pagedFailure, pagedMoreFailure, pagedMoreRequest, pagedMoreSuccess, pagedPatch, pagedRequest, pagedSuccess } from './pagedListModel';
import type { PageAnswer, PagedState } from './pagedListModel';

export interface PagedListDeps<T> {
  /** The current Caller epoch (`workApi.getCallerEpoch`), read when a request starts. */
  getEpoch: () => number;
  /** Reads one page: the first when `cursor` is null, otherwise the page the cursor names. */
  fetchPage: (cursor: string | null) => Promise<PageAnswer<T>>;
  /** The identity of an item (to avoid repeating one across pages, and to patch it). */
  keyOf: (item: T) => string;
  /** True when a failure means "this server does not serve the route" (isRouteUnavailable). Default: never. */
  isUnavailable?: (thrown: unknown) => boolean;
  /** Default true. See the header. */
  reloadOnCallerChange?: boolean;
}

export interface PagedList<T> {
  getState: () => PagedState<T>;
  subscribe: (listener: () => void) => () => void;
  load: () => Promise<void>;
  ensureLoaded: () => Promise<void>;
  loadMore: () => Promise<void>;
  patch: (key: string, update: (item: T) => T) => void;
  callerChanged: () => Promise<void>;
}

export function createPagedList<T>(deps: PagedListDeps<T>): PagedList<T> {
  let state: PagedState<T> = initialPaged<T>(deps.getEpoch());
  // A view has asked for this list at least once; only then does a Caller change read it again by itself.
  let wanted = false;
  const listeners = new Set<() => void>();
  const unavailable = deps.isUnavailable ?? (() => false);
  const reload = deps.reloadOnCallerChange !== false;

  function publish(next: PagedState<T>): void {
    if (next === state) return;
    state = next;
    for (const listener of [...listeners]) listener();
  }

  async function load(): Promise<void> {
    wanted = true;
    const started = pagedRequest(state, deps.getEpoch());
    publish(started.state);
    const epoch = started.state.epoch;
    const seq = started.seq;
    let page: PageAnswer<T>;
    try {
      page = await deps.fetchPage(null);
    } catch (thrown) {
      // Dropped because the Caller changed: neither data nor a failure. The Caller change has already cleared this list.
      if (isStaleOutcome(thrown)) return;
      publish(pagedFailure(state, epoch, seq, thrown, unavailable(thrown)));
      return;
    }
    publish(pagedSuccess(state, epoch, seq, page));
  }

  async function loadMore(): Promise<void> {
    const step = pagedMoreRequest(state);
    if (!step.send) return;
    publish(step.state);
    const epoch = state.epoch;
    const seq = state.seq;
    let page: PageAnswer<T>;
    try {
      page = await deps.fetchPage(step.cursor);
    } catch (thrown) {
      if (isStaleOutcome(thrown)) return;
      publish(pagedMoreFailure(state, epoch, seq, thrown));
      return;
    }
    publish(pagedMoreSuccess(state, epoch, seq, page, deps.keyOf));
  }

  return {
    getState: () => state,
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    load,
    async ensureLoaded() {
      if (state.phase === 'idle') await load();
    },
    loadMore,
    patch(key, update) {
      publish(pagedPatch(state, key, deps.keyOf, update));
    },
    async callerChanged() {
      publish(initialPaged<T>(deps.getEpoch()));
      if (wanted && reload) await load();
    },
  };
}
